/** The model's estimate as every result surface shows it: to the
 *  thousand for a sale, to the ten for a rent (the result hero, the
 *  gauge and the compare board all use this grain). */
export function roundEstimate(value: number, rent: boolean): number {
  const grain = rent ? 10 : 1000;
  return Math.round(value / grain) * grain;
}

/** The fair zone's half-width as a share of the estimate: half the
 *  model's band, clamped to 5..15% (analyzer_service.fair_half_of, the
 *  verdict's own arithmetic). The floor when there is no usable band. */
export const FAIR_HALF_MIN = 0.05;
export const FAIR_HALF_MAX = 0.15;

export function fairHalf(predicted: number, low: number | null | undefined, high: number | null | undefined): number {
  if (!(predicted > 0) || low == null || high == null || !(high > low)) return FAIR_HALF_MIN;
  return Math.max(FAIR_HALF_MIN, Math.min(FAIR_HALF_MAX, (high - low) / 2 / predicted));
}

export interface FairZone {
  low: number;
  high: number;
  /** Half-width as a share of the estimate (0.05..0.15). */
  half: number;
}

/**
 * The range every surface prints: the fair zone the verdict tier is made
 * from, never the 80% model band (that band contained the asking price
 * of a flat the verdict called overpriced; wave 3 review, 2026-10-25).
 * The backend's fair_low / fair_high when the payload carries them;
 * pages cached before they shipped derive the same zone from the band
 * with the same formula; without a band the zone is the 5% floor.
 * Null without a positive estimate.
 */
export function fairZone(p: {
  predicted_price: number | null | undefined;
  fair_low?: number | null;
  fair_high?: number | null;
  predicted_price_low?: number | null;
  predicted_price_high?: number | null;
}): FairZone | null {
  const pred = p.predicted_price ?? 0;
  if (!(pred > 0)) return null;
  if (p.fair_low != null && p.fair_high != null && p.fair_high > p.fair_low) {
    return { low: p.fair_low, high: p.fair_high, half: (p.fair_high - p.fair_low) / 2 / pred };
  }
  const half = fairHalf(pred, p.predicted_price_low, p.predicted_price_high);
  return { low: Math.round(pred * (1 - half)), high: Math.round(pred * (1 + half)), half };
}
